/**
 * TrialCountdownStrip
 *
 * Sticky amber/red strip shown at the very top of every shop page during:
 *   - The last 7 days of a free trial.
 *   - The last 7 days before a paid-subscription expiry.
 *   - Any time the subscription has already expired (until renewed).
 *
 * Per-day dismissal: once a shop owner closes the strip we set a sessionStorage
 * key that includes today's date, so they aren't nagged on every navigation —
 * but the strip reappears the next calendar day.
 *
 * Different copy for trial vs paid subscriber vs founding member.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Clock, AlertTriangle, X, ArrowRight } from "lucide-react";

const todayKey = () => new Date().toISOString().slice(0, 10);
const DISMISS_BASE = "hp_countdown_dismiss_";

export default function TrialCountdownStrip({ usage }) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset dismissed flag on a new calendar day
    try {
      const k = DISMISS_BASE + todayKey();
      if (sessionStorage.getItem(k)) setDismissed(true);
    } catch (_) { /* ignore */ }
  }, [usage?.days_left]);

  if (!usage) return null;
  const days = usage.days_left;
  const expired = !!usage.expired;
  // Only render in the last-7-day window or once expired.
  if (days === null || days === undefined) return null;
  if (!expired && days > 7) return null;
  if (dismissed) return null;

  const isTrial = usage.plan_id === "trial";
  const isFounding = !!usage.is_founding_member;

  // Founding members in the warning window keep an amber tone (their lock-in
  // is still safe within the 60-day grace); they only see the "renew gently"
  // copy. Non-founding shops past expiry get the red emergency tone.
  const tone = expired
    ? "bg-red-600 text-white border-red-700"
    : days <= 3
    ? "bg-amber-500 text-amber-950 border-amber-600"
    : "bg-amber-50 text-amber-900 border-amber-300";

  const Icon = expired ? AlertTriangle : Clock;

  let message;
  if (expired) {
    message = isTrial
      ? "Aap ka trial khatam ho gaya hai — abhi upgrade karein."
      : "Aap ki subscription expire ho gayi — abhi renew karein.";
  } else if (isTrial && !isFounding) {
    message = `Aap ka trial ${days} din mein expire ho raha hai. Founding Member ban kar PKR 2,999/quarter lifetime lock karein.`;
  } else if (isTrial) {
    message = `Aap ka trial ${days} din mein expire ho raha hai. Plan select karein continue rakhne ke liye.`;
  } else {
    message = `Aap ki subscription ${days} din mein expire ho rahi hai. Service break se bachne ke liye renew karein.`;
  }

  const ctaLabel = isTrial && !isFounding ? "Become Founding Member" : "Upgrade Now";

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_BASE + todayKey(), "1"); } catch (_) { /* */ }
    setDismissed(true);
  };

  return (
    <div
      className={`flex items-center gap-2 sm:gap-3 border-b px-3 sm:px-5 py-2 ${tone}`}
      data-testid="trial-countdown-strip"
      role="status"
    >
      <Icon className="w-4 h-4 shrink-0" />
      <div className="flex-1 min-w-0 text-xs sm:text-sm font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
        {isTrial && !isFounding && !expired && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
            <Crown className="w-3 h-3" /> Limited Offer
          </span>
        )}
        <span className="line-clamp-2 sm:line-clamp-1" data-testid="strip-message">{message}</span>
      </div>
      <Link
        to="/subscription"
        className={`shrink-0 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-bold transition active:scale-95 ${
          expired
            ? "bg-white text-red-700 hover:bg-red-50"
            : days <= 3
            ? "bg-white text-amber-900 hover:bg-amber-50"
            : "bg-amber-700 text-white hover:bg-amber-800"
        }`}
        data-testid="strip-cta"
      >
        {ctaLabel} <ArrowRight className="w-3 h-3" />
      </Link>
      <button
        onClick={dismiss}
        className="shrink-0 -mr-1 p-1.5 rounded hover:bg-black/10 transition"
        aria-label="Dismiss reminder"
        data-testid="strip-dismiss"
      >
        <X className="w-4 h-4 opacity-70" />
      </button>
    </div>
  );
}
